import { ValidationError } from 'yup';
import { useMutation } from '@tanstack/react-query';
import { Appointments } from '@prisma/client';
import { removeFromQueue, invalidateQueue } from '../../services/queue';
import {
  RemoveFromQueuePayload,
  removeFromQueueSchema,
} from '../../types/queue';

type Payload = {
  appointmentId: string;
  payload: RemoveFromQueuePayload;
};

interface Options {
  onSuccess?: (
    appointment: Appointments,
    variables: Payload,
  ) => void | Promise<void>;
  onError?: (message: string) => void | Promise<void>;
  onMutate?: () => void | Promise<void>;
  onSettled?: () => void | Promise<void>;
}

function useRemoveFromQueue({
  onSuccess,
  onError,
  onMutate,
  onSettled,
}: Options = {}) {
  return useMutation({
    mutationFn: async ({ appointmentId, payload }: Payload) => {
      const validatedPayload = await removeFromQueueSchema
        .noUnknown()
        .validate(payload, {
          stripUnknown: true,
        });

      return removeFromQueue(appointmentId, validatedPayload);
    },
    onSuccess: async (appointment, variables) => {
      await invalidateQueue();

      await onSuccess?.(appointment, variables);
    },
    onError: (error: Error) => {
      if (error instanceof ValidationError) {
        onError?.(error.message);
        return;
      }
      onError?.('Failed to remove appointment from queue');
    },
    onMutate,
    onSettled,
  });
}

export default useRemoveFromQueue;
